import clsx from 'clsx';
import React, { createContext, useContext } from 'react';

type AccordionHeaderToggleProps = {
  contentId: string;
  isDisabled: boolean;
  isExpanded: boolean;
};

/** Id del panel del item de AccordionList2 en cuya cabecera se pinta. */
export const AccordionItemContentIdContext = createContext<string | undefined>(undefined);

export function getAccordionItemLabelId(contentId: string): string {
  return `${contentId}-label`;
}

/** Id que debe llevar el título de un item con `hasInnerToggle`: da nombre a su conmutador. */
export function useAccordionItemLabelId(): string | undefined {
  const contentId = useContext(AccordionItemContentIdContext);
  return contentId ? getAccordionItemLabelId(contentId) : undefined;
}

type InnerToggleSummaryProps = {
  'aria-disabled': undefined;
  'aria-expanded': undefined;
  className: string;
  role: undefined;
  tabIndex: number;
};

/** Props que quitan al `AccordionSummary` su papel de botón: el conmutador pasa a ser AccordionHeaderToggle. */
export function getInnerToggleSummaryProps(className?: string): InnerToggleSummaryProps {
  return {
    'aria-disabled': undefined,
    'aria-expanded': undefined,
    className: clsx('accordion-header-toggle__host', className),
    role: undefined,
    tabIndex: -1,
  };
}

type AccordionItemLabelProps = Readonly<{
  children: React.ReactNode;
  className?: string;
}>;

/** Título de un item con `hasInnerToggle` cuyo `title` se construye fuera del acordeón, donde el hook aún no ve el contexto. */
export function AccordionItemLabel({ children, className }: AccordionItemLabelProps): JSX.Element {
  const labelId = useAccordionItemLabelId();

  return (
    <span className={className} id={labelId}>
      {children}
    </span>
  );
}

/**
 * AccordionHeaderToggle - Conmutador de un item de AccordionList2 con `hasInnerToggle`, es decir,
 * cuya cabecera contiene otros controles («Más opciones», «Ver kanban»…).
 *
 * `AccordionSummary` es un `role="button"`: cualquier control dentro de él es `nested-interactive`
 * (WCAG 4.1.2). En su lugar, la cabecera queda sin rol ni parada de Tab y este botón vacío la cubre
 * (patrón overlay, TAN-9855), con el título como nombre (`aria-labelledby`). No recibe el puntero:
 * los clics siguen llegando a la cabecera y a sus controles, y Enter/Espacio generan su clic nativo.
 */
function AccordionHeaderToggle({ contentId, isDisabled, isExpanded }: AccordionHeaderToggleProps): JSX.Element {
  return (
    <button
      type="button"
      aria-controls={contentId}
      aria-expanded={isExpanded}
      aria-labelledby={getAccordionItemLabelId(contentId)}
      className="accordion-header-toggle"
      disabled={isDisabled}
    />
  );
}

export default AccordionHeaderToggle;
